import {
  getNestedStateObject
} from '@/utils';

const runValidation = (fieldData, data) => {
  // reset our custom validation each time
  if (data.target && typeof data.target.setCustomValidity === 'function') {
    data.target.setCustomValidity('');
  }

  // check initial validity
  let valid = false;

  // if its a radio button, check at least one is valid
  if (Array.isArray(data.target)) {
    valid = data.target.some(target => target.checkValidity());
  } else if (data.target && typeof data.target.checkValidity === 'function') {
    valid = data.target.checkValidity();
  }

  // run customValidation function if needed
  if (valid && fieldData.customValidation) {
    return fieldData.customValidation(data.target);
  }

  return {
    valid,
    message: data.target && data.target.value ? data.target.title : ''
  };
}

const writeValidatedField = (state, data, setUserHasTyped) => {
  const fieldData = getNestedStateObject(state, [...data.vuexPath, 'fields', data.name]);
  if (fieldData?.serverBookingError) {
    return;
  }
  const validationStatus = runValidation(fieldData, data);
  const fieldsObj = getNestedStateObject(state, [...data.vuexPath, 'fields']);
  if (!fieldsObj) {
    return;
  }
  fieldsObj[data.name] = {
    ...fieldData,
    error: !validationStatus.valid,
    message: validationStatus.message,
    ...(setUserHasTyped ? { userHasTyped: true } : {})
  };
};

export default {
  /**
   * setup a form field (and the main form object if needed)
   * sets nested state data to ensure things don't break
   */
  setupField(state, data) {
    // create nested objects (where necessary) until we reach the desired place
    let formData = state;
    for (let i = 0; i < data.vuexPath.length; i++) {
      if (typeof formData[data.vuexPath[i]] === 'undefined' || formData[data.vuexPath[i]] === null) {
        formData[data.vuexPath[i]] = {};
      }
      formData = formData[data.vuexPath[i]];
    }

    // set the form state and the field to the fields object
    if (typeof formData.summaryView === 'undefined') {
      formData.summaryView = false;
    }
    if (typeof formData.hasBeenSubmitted === 'undefined') {
      formData.hasBeenSubmitted = false;
    }
    if (typeof formData.fields === 'undefined') {
      formData.fields = {};
    }
    const existing = formData.fields[data.name];
    if (existing) {
      formData.fields[data.name] = {
        ...existing,
        value: data.value,
        required: data.required,
        customValidation: data.customValidation ?? existing.customValidation
      };
      return;
    }
    formData.fields[data.name] = {
      value: data.value,
      error: data.required,
      message: '',
      customValidation: data.customValidation,
      userHasTyped: false
    };
  },

  destroyField(state, data) {
    const formData = getNestedStateObject(state, [...data.vuexPath, 'fields']);
    if (formData) {
      // Vue 3 compatible: direct deletion works with reactivity
      delete formData[data.name];

      // if we have deleted all fields
      // reset the main form hasBeenSubmitted to false
      if (!Object.keys(formData).length) {
        // Vue 3 compatible: direct assignment works with reactivity
        const nestedState = getNestedStateObject(state, [...data.vuexPath]);
        if (nestedState) nestedState.hasBeenSubmitted = false;
      }
    }
  },

  /** set a single field value */
  setFieldValue(state, data) {
    // Vue 3 compatible: direct assignment works with reactivity
    const fieldObj = getNestedStateObject(state, [...data.vuexPath, 'fields', data.name]);
    if (fieldObj) fieldObj.value = data.value;
  },

  /** set field values from a flat object (also handles nesting) */
  setFieldValuesFromFlat(state, data) {
    const formData = getNestedStateObject(state, data.vuexPath);

    const setFieldValuesFromFlat = (object, data) => {
      for (const fieldName in data) {
        if (fieldName === 'fields') {
          continue;
        }

        if (typeof data[fieldName] === 'object' && data[fieldName] !== null) {
          if (object.fields[fieldName]) {
            setFieldValuesFromFlat(object.fields[fieldName], data[fieldName]);
          } else if (data[fieldName].fields) {
            setFieldValuesFromFlat(data[fieldName], data[fieldName]);
          }
        } else if (object.fields[fieldName]) {
          // Vue 3 compatible: direct assignment works with reactivity
          object.fields[fieldName].value = data[fieldName];
        }
      }
    }

    setFieldValuesFromFlat(formData, data.flat);
  },

  /** set error status on field */
  setFieldError(state, data) {
    writeValidatedField(state, data, false);
  },

  clearServerBookingValidationOnField(state, data) {
    const field = getNestedStateObject(state, [...data.vuexPath, 'fields', data.name]);
    if (field?.serverBookingError) {
      field.serverBookingError = false;
    }
  },

  /** validate a single field */
  validateField(state, data) {
    writeValidatedField(state, data, true);
  },

  /** set the has been submitted boolean */
  setHasBeenSubmitted(state, vuexPath) {
    const formData = getNestedStateObject(state, vuexPath);
    // Vue 3 compatible: direct assignment works with reactivity
    formData.hasBeenSubmitted = true;

    // submit address if it exists
    if (formData.address && formData.address.fields) {
      // Vue 3 compatible: direct assignment works with reactivity
      formData.address.hasBeenSubmitted = true;
      formData.address.fields.addressLines.hasBeenSubmitted = true;
    }
  },

  /** set the has been submitted boolean */
  unsetHasBeenSubmitted(state, vuexPath) {
    const formData = getNestedStateObject(state, vuexPath);
    // Vue 3 compatible: direct assignment works with reactivity
    formData.hasBeenSubmitted = false;

    // unsubmit address if it exists
    if (formData.address && formData.address.fields) {
      // Vue 3 compatible: direct assignment works with reactivity
      formData.address.hasBeenSubmitted = false;
      formData.address.fields.addressLines.hasBeenSubmitted = false;
    }
  },

  /** set summary view boolean */
  setSummaryView(state, vuexPath) {
    // Vue 3 compatible: direct assignment works with reactivity
    const nestedState = getNestedStateObject(state, vuexPath);
    if (nestedState) nestedState.summaryView = true;
  },


  /** unset summary view boolean */
  unsetSummaryView(state, vuexPath) {
    // Vue 3 compatible: direct assignment works with reactivity
    const nestedState = getNestedStateObject(state, vuexPath);
    if (nestedState) nestedState.summaryView = false;
  }
}